import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from 'src/lib/utils'

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        // `border-border`, where stock shadcn writes a bare `border`. Tailwind
        // v4's initial `border-color` is `currentColor`, so without a colour
        // this border drew near-black — defect S2 on the reference branch.
        // `src/styles/tailwind.css` supplies the colour as a base rule, but
        // narrowed to `[data-slot]` rather than the universal selector shadcn's
        // own globals.css uses, because antd owns the DOM with the toggle off.
        // Naming it here is belt-and-braces for that narrowing and costs
        // nothing — a utility outranks the rule either way, which is also why
        // the reference branch's seams carry the same explicit utilities. See
        // `src/styles/__tests__/border-utilities-name-a-colour.test.ts`.
        outline:
          'border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

// forwardRef (not React-19 ref-as-prop): the runtime here is React 18, and
// antd overlays that clone this button as their trigger (Tooltip, Dropdown,
// Popconfirm) attach a ref to it — without forwardRef React logs "Function
// components cannot be given refs" and the overlay can't anchor. The semantic
// button kit (src/storybook/components/Buttons) relies on this.
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      asChild = false,
      ...props
    },
    ref
  ) => {
    // `React.ElementType` on purpose: this repo pins @types/react 17 (see
    // package.json resolutions), whose `LegacyRef` includes a string ref that
    // clashes with Radix `Slot`'s `Ref<HTMLElement>` when the two are unioned.
    // Typing Comp loosely sidesteps that React-17-types clash; runtime is
    // unchanged. Revert to the stock shadcn typing once @types/react is on 18.
    const Comp: React.ElementType = asChild ? Slot.Root : 'button'

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
